/**
 * Authored-HTML snippets for the block palette. The Tailwind classes are
 * literal strings in this source file on purpose: the build-time scanner picks
 * them up here, so palette-inserted blocks render with real compiled styles.
 *
 * Text sizes are inline `font-size` with a unitless `line-height` rather than a
 * `text-*` class, which pins the leading to a fixed rem value the properties
 * panel's size control cannot follow.
 *
 * Line/totals/signature content in vocabulary-v2 lives entirely inside repeater
 * templates (fp-lines and friends), which the palette does not insert: item
 * rows come from the authored template and are edited as columns instead.
 *
 * Headings and lists have no entry of their own: a text block grows them from
 * their markdown shorthand ("# ", "- "), see textEditExtensions.ts.
 */

import { FP_ORG_LOGO_ASSET_ID } from "@forgepoint/shared";
import { isMarkdownQuoteField } from "./quoteFields";

/** One insertable palette block. */
export interface PaletteBlock {
  /** Stable identifier for the palette entry. */
  id: string;
  /** Button label. */
  label: string;
  /** The authored HTML inserted into the document (single root element). */
  html: string;
}

/**
 * The insertable blocks offered by the palette, in display order. Every block
 * that holds prose is seeded with placeholder copy rather than left empty: an
 * empty `<fp-text>` renders nothing at all, so an empty column would land on
 * the canvas with no box to click and no way back to it - the author would have
 * to delete a block they cannot see.
 *
 * The table's cells carry bare cell text: a table cell owns its text rather
 * than nesting blocks, and inline editing resolves the cell itself
 * (renderedTextEdit.ts).
 *
 * The columns row wraps each column in an `<fp-cell>` because a columns-column
 * cell is a stack, so the column holds any number of any blocks - an image
 * beside a table - rather than only the one block a bare column child stands
 * in for.
 */
export const PALETTE_BLOCKS: readonly PaletteBlock[] = [
  {
    id: "text",
    label: "Text",
    html: "<fp-text>New text</fp-text>",
  },
  {
    id: "columns",
    label: "Columns",
    html: `<fp-columns widths="50%,50%" gap="16px">
<fp-cell><fp-text>Left column</fp-text></fp-cell>
<fp-cell><fp-text>Right column</fp-text></fp-cell>
</fp-columns>`,
  },
  {
    id: "table",
    label: "Table",
    html: `<fp-table rules="grid" border="1px solid #d1d5db">
<fp-column width="50%"></fp-column>
<fp-column width="50%"></fp-column>
<fp-row>
<fp-cell>Cell</fp-cell>
<fp-cell>Cell</fp-cell>
</fp-row>
<fp-row>
<fp-cell>Cell</fp-cell>
<fp-cell>Cell</fp-cell>
</fp-row>
</fp-table>`,
  },
  {
    id: "signature",
    label: "Sales rep signature",
    html: '<fp-field source="sales-rep-name" class="font-signature block text-gray-900" style="font-size: 24px; line-height: 1"></fp-field>',
  },
  {
    id: "image",
    label: "Image",
    html: '<fp-image height="64" class="my-4"></fp-image>',
  },
  {
    id: "org-logo",
    label: "Organization logo",
    html: `<fp-image asset-id="${FP_ORG_LOGO_ASSET_ID}" class="my-4"></fp-image>`,
  },
  {
    id: "divider",
    label: "Divider",
    html: "<fp-divider></fp-divider>",
  },
  {
    id: "page-number",
    label: "Page number",
    html: '<fp-page-number format="page-n-of-total"></fp-page-number>',
  },
  {
    id: "page-break",
    label: "Page break",
    html: "<fp-page-break></fp-page-break>",
  },
  {
    id: "page-frame",
    label: "Page frame",
    html: '<fp-page-frame border="1px solid #111827" margin="24px"></fp-page-frame>',
  },
  {
    id: "header",
    label: "Header",
    html: `<fp-header padding="0 0 8px 0">
<fp-columns widths="30%,70%" gap="16px">
<fp-cell><fp-image asset-id="org-logo" height="64"></fp-image></fp-cell>
<fp-cell><fp-text style="font-size: 12px; line-height: 1.5">{{ org-address }}

Fax: {{ org-fax }} · Tel: {{ org-phone }}
Website: {{ org-website }}</fp-text></fp-cell>
</fp-columns>
</fp-header>`,
  },
  {
    id: "footer",
    label: "Footer",
    html: `<fp-footer padding="8px 0 0 0">
<fp-columns widths="50%,50%" gap="16px">
<fp-cell><fp-text style="font-size: 10px; line-height: 1.4">{{ org-name }}</fp-text></fp-cell>
<fp-cell align="right"><fp-page-number format="page-n-of-total" style="font-size: 10px; line-height: 1.4"></fp-page-number></fp-cell>
</fp-columns>
</fp-footer>`,
  },
];

/**
 * Builds the authored HTML for a standard-field block bound to a quote field.
 * Markdown-format sources (terms of service, payment terms, ...) insert a
 * bound `<fp-markdown>` instead of `<fp-field>` so their multiline markdown
 * renders as prose (headings, lists) rather than flat text; the tag carries no
 * class so the default markdown prose styling applies.
 * @param source - The quote field key (see quoteFields.ts)
 * @returns The fp-markdown block HTML for markdown sources, else fp-field
 */
export function buildFieldBlockHtml(source: string): string {
  const escaped = escapeAttributeValue(source);
  if (isMarkdownQuoteField(source)) {
    return `<fp-markdown source="${escaped}"></fp-markdown>`;
  }
  return `<fp-field source="${escaped}" class="block text-gray-800" style="font-size: 12px; line-height: 1.4"></fp-field>`;
}

/**
 * Escapes a value for use inside a double-quoted HTML attribute.
 * @param value - The raw attribute value
 * @returns The value with & and " replaced by entities
 */
function escapeAttributeValue(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}
